/**
 * The pun that `punPreview` hands to the page it serves.
 *
 * `/p/{punId}` has to work for someone with no account — that is the entire
 * point of a shared link. But the author's display name lives in
 * `users/{userId}`, which `firestore.rules` gates behind `isSignedIn()`, and
 * opening that collection to the public would expose follow lists, reminder
 * times and time zones along with the name.
 *
 * The server already has the answer. `punPreview` reads the pun and resolves the
 * author with the Admin SDK to build the card and the meta tags, so it can embed
 * the same data in the document it is already serving. The page then renders
 * with no Firestore read at all — no permission to deny, no App Check token to
 * mint, and no spinner.
 *
 * Shared so the two sides cannot drift on the element id or the shape.
 */
import { z } from 'zod';

/** The `<script>` element carrying the payload. */
export const SHARED_PUN_ELEMENT_ID = 'shared-pun';

export const sharedPunSchema = z.object({
  id: z.string(),
  text: z.string(),
  category: z.string(),
  output: z.string(),
  authorName: z.string(),
  crowns: z.number().int().nonnegative(),
});

export type SharedPunPayload = z.infer<typeof sharedPunSchema>;

/**
 * The payload as the contents of a `<script type="application/json">`.
 *
 * `<` is escaped to `<`, which is what stops a pun containing `</script>`
 * from closing the tag early and turning the rest of the page into markup. JSON
 * treats `<` as identical to `<`, so nothing is lost on the way back —
 * unlike HTML entity escaping, which would survive into the parsed string.
 */
export function serializeSharedPun(pun: SharedPunPayload): string {
  return JSON.stringify(pun).replace(/</g, '\\u003c');
}

/** Parse a payload back, or null if it is absent or malformed. */
export function parseSharedPun(json: string | null | undefined): SharedPunPayload | null {
  if (!json) return null;

  try {
    const result = sharedPunSchema.safeParse(JSON.parse(json));
    return result.success ? result.data : null;
  } catch {
    // Not JSON at all. The page still has the Firestore fallback.
    return null;
  }
}
